import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { getAPIUrl } from "../../config/api";
import { onPublicTokenChange } from "../slices/auth";
import { onPatientSessionReset } from "../slices/order";

import type { RootState } from "..";

const baseUrl = getAPIUrl();

export const api = createApi({
  reducerPath: "store",
  baseQuery: async (args, api, extraOptions) => {
    const baseQuery = fetchBaseQuery({
      baseUrl,
      credentials: "include",
      prepareHeaders: async (headers, { getState }) => {
        const { token } = (getState() as RootState).authSlice;
        const { currentAccount } = (getState() as RootState).userSlice;

        if (token) {
          headers.set("authorization", `Bearer ${token}`);
        }

        if (currentAccount) {
          headers.set("account", currentAccount);
        }

        return headers;
      },
    });

    const result = await baseQuery(args, api, extraOptions);
    if (result.error && result.error.status === 401) {
      window.location.href = "/login";
    }
    return result;
  },
  tagTypes: [
    "Chat",
    "ChatModel",
    "ChatFile",
    "BaseRateFiles",
    "BaseRateOverrideItems",
    "BaseFiles",
    "ContractFeeSchedules",
    "MarketPrices",
    "ContractInsights",
    "EdiConversion",
    "DirectPayLinks",
    "Claims",
    "PromptVersions",
    "InpatientRulesets",
    "ContractHierarchyRules",
    "FinixOnboarding",
    "PrecalculatedAllowables",
    "CustomCptVolume",
    "MpprRulesets",
    "PackageRulesets",
    "CaseRateRulesets",
    "Account",
    "ExplodingFeeScheduleRulesets",
    "ProfessionalRulesets",
    "InstitutionalRulesets",
    "EndoFamilies",
    "ModifierAdjustmentRulesets",
    "LesserOfChargesRulesets",
    "ChargeBasedPricingRulesets",
    "UnitCapRulesets",
    "DefaultRulePropagation",
    "ServiceLineSynonyms",
    "PayerProcedureServiceLines",
    "PayerGrouperSets",
    "ContractModelDrafts",
  ],
  endpoints: () => ({}),
  keepUnusedDataFor: 0,
});

export const publicApi = createApi({
  reducerPath: "publicStore",
  baseQuery: async (args, api, extraOptions) => {
    const baseQuery = fetchBaseQuery({
      baseUrl,
      credentials: "include",
      prepareHeaders: async (headers, { getState }) => {
        const { publicToken } = (getState() as RootState).authSlice;

        if (publicToken) {
          headers.set("authorization", `Bearer ${publicToken}`);
        }

        return headers;
      },
    });

    const result = await baseQuery(args, api, extraOptions);

    // CLE-2181: patient tokens are order-bound and expire, so the API can now answer 403 with a
    // PATIENT_TOKEN_* code. Previously only 401 was handled — and it redirects to /login, which
    // is the STAFF login and wrong for a patient. Drop the dead token and send the patient back
    // to the date-of-birth gate so they have a way to recover.
    if (result.error && result.error.status === 403) {
      const code = (result.error.data as { code?: string } | undefined)?.code;
      if (typeof code === "string" && code.startsWith("PATIENT_TOKEN_")) {
        api.dispatch(onPublicTokenChange({ token: "" }));
        api.dispatch(onPatientSessionReset());
      }
    }
    if (result.error && result.error.status === 401) {
      window.location.href = "/login";
    }
    return result;
  },
  tagTypes: [
    "Chat",
    "ChatModel",
    "ChatFile",
    "BaseRateFiles",
    "BaseRateOverrideItems",
    "BaseFiles",
    "ContractFeeSchedules",
    "MarketPrices",
    "ContractInsights",
    "EdiConversion",
    "DirectPayLinks",
    "Claims",
    "PromptVersions",
    "CustomCptVolume",
  ],
  endpoints: () => ({}),
  keepUnusedDataFor: 0,
});
